/**
 * One arrangement run, from pressing Arrange to the score coming back: Arrange's and the Lab board's
 * side of the server's job routes.
 *
 * A run is a job on the server because a whole movement takes tens of seconds. The score goes first:
 * a library score by its id, which the server reads from its own disk, and anything else as
 * an upload of its bytes. The job is then started and polled until it is done, failed or
 * cancelled, and what the pipeline last said it was doing is kept for the page to show.
 *
 * Leaving the board cancels a run still going. Nothing would ever collect it, and a movement
 * nobody is waiting for is a processor kept busy for nothing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  UPLOAD_LIMIT,
  type ArrangePayload,
  type ArrangeProgress,
  type ArrangeRequest,
  type RecogniseProgress,
} from "@app/shared-types";
import { api } from "../api/client";
import { bytes } from "./format";
import { followed, said } from "./job-poll";

/** What the page asks for: the score, and everything else the request carries. */
export interface ArrangeAsk extends Omit<ArrangeRequest, "upload" | "score"> {
  /** A library score's id, when the score came from the library; it is then not uploaded. */
  readonly library: string | null;
  /** The score's text, uploaded when it did not come from the library. */
  readonly text: string;
}

/**
 * Where a run stands, as the page draws it. `uploading` is before the server has a job; a
 * scan being read into a score (`lib/arrange-open.ts`) is drawn by the same strip.
 */
export type RunProgress = ArrangeProgress | RecogniseProgress | { readonly stage: "uploading" };

export interface ArrangeJobState {
  /** What the run in progress last reported, or `null` when nothing is running. */
  readonly progress: RunProgress | null;
  readonly result: ArrangePayload | null;
  readonly error: string | null;
  run(ask: ArrangeAsk): void;
  cancel(): void;
  /** Forget the last run's result and error, as when another score is opened. */
  clear(): void;
}

export function useArrangeJob(): ArrangeJobState {
  const [progress, setProgress] = useState<RunProgress | null>(null);
  const [result, setResult] = useState<ArrangePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Each run gets a number, and every step of it checks that it is still the latest before it
  // touches the page: a cancelled run, or one replaced by a newer one, must not land late.
  const generation = useRef(0);
  const job = useRef<string | null>(null);

  const stop = useCallback((): void => {
    generation.current += 1;
    const running = job.current;
    job.current = null;
    if (running !== null) void api.cancelArrange(running).catch(() => undefined);
  }, []);

  // A run asked for once the page is gone, as by an answer in words that lands late, would be
  // arranged to the end with nobody left to cancel it.
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
      stop();
    };
  }, [stop]);

  const run = useCallback(
    (ask: ArrangeAsk): void => {
      if (gone.current) return;
      stop();
      const mine = generation.current;
      const live = (): boolean => generation.current === mine;
      setResult(null);
      setError(null);

      const start = async (): Promise<void> => {
        const { library, text, ...rest } = ask;
        let source: Pick<ArrangeRequest, "upload" | "score"> = { score: library ?? undefined };
        if (library === null) {
          const blob = new Blob([text], { type: "application/octet-stream" });
          if (blob.size > UPLOAD_LIMIT) {
            throw new Error(
              `this score is ${bytes(blob.size)}; a score to arrange may run to ${bytes(UPLOAD_LIMIT)}`,
            );
          }
          setProgress({ stage: "uploading" });
          source = { upload: (await api.upload(blob)).upload };
        }
        if (!live()) return;
        setProgress({ stage: "queued" });
        const started = await api.arrange({ ...rest, ...source });
        if (!live()) {
          // Cancelled while the job was being started: it exists now, and nobody will poll it.
          void api.cancelArrange(started.job).catch(() => undefined);
          return;
        }
        job.current = started.job;
        const made = await followed(() => api.arrangeJob(started.job), live, setProgress);
        if (!live()) return;
        job.current = null;
        setProgress(null);
        if (made !== null) setResult(made);
      };

      start().catch((cause: unknown) => {
        if (!live()) return;
        job.current = null;
        setProgress(null);
        setError(said(cause));
      });
    },
    [stop],
  );

  const cancel = useCallback((): void => {
    stop();
    setProgress(null);
  }, [stop]);

  const clear = useCallback((): void => {
    stop();
    setProgress(null);
    setResult(null);
    setError(null);
  }, [stop]);

  return { progress, result, error, run, cancel, clear };
}
